/**
 * Navigation utilities for the widget
 */

import {
    getIndexUrl,
    getAuctionListFullUrl,
    getAuctioneerListUrl
} from './urls';

import {
    getFeature,
    FEATURE__SHOWCASE,
    FEATURE__AUCTIONEERS_ONLY
} from './features';

/**
 * Detect current page type from pathname
 */
export const getPageType = (pathname) => {
    if (!pathname || pathname === '/') return 'home';
    if (pathname.startsWith('/auction/')) return 'auction';
    if (pathname.startsWith('/auctions')) return 'auctions';
    if (pathname.startsWith('/past')) return 'past-auctions';
    if (pathname.startsWith('/profile/')) return 'auctioneer';
    if (pathname.startsWith('/members')) return 'auctioneers';
    return 'unknown';
};

/**
 * Get navigation items based on current location
 */
export const getNavItems = (pathname, options = {}) => {
    const { lockToAuctioneer } = options;
    const pageType = getPageType(pathname);
    const showcase = getFeature(FEATURE__SHOWCASE);
    const auctioneersOnly = getFeature(FEATURE__AUCTIONEERS_ONLY);

    const items = [];

    // Don't show nav in showcase mode or if only showing auctioneers
    if (showcase || auctioneersOnly) return items;

    // Home/Auctions link (always show unless locked to auctioneer)
    if (!lockToAuctioneer) {
        items.push({
            label: 'Auctions',
            url: getIndexUrl(),
            active: pageType === 'home' || pageType === 'auctions'
        });

        // Auctioneers link
        items.push({
            label: 'Auctioneers',
            url: getAuctioneerListUrl(),
            active: pageType === 'auctioneers'
        });
    }

    return items;
};

/**
 * Get back link info based on current page
 */
export const getBackLink = (pathname, options = {}) => {
    const { lockToAuctioneer, lastListUrl } = options;
    const pageType = getPageType(pathname);
    const auctioneersOnly = getFeature(FEATURE__AUCTIONEERS_ONLY);

    switch (pageType) {
        case 'auction':
            return {
                label: lastListUrl && getPageType(lastListUrl) == 'auctioneers' ? 'All Auctioneers' : 'All Auctions',
                // Use saved URL if available, otherwise default to page 1
                url: lockToAuctioneer ? getIndexUrl() : (lastListUrl || getAuctionListFullUrl())
            };
        case 'auctioneer':
            return {
                label: lockToAuctioneer ? 'Back' : 'All Auctioneers',
                url: lockToAuctioneer ? getIndexUrl() : getAuctioneerListUrl()
            };
        default:
            return null;
    }
};

/**
 * Get breadcrumb items for current page
 */
export const getBreadcrumbs = (pathname, pageTitle, options = {}) => {
    const { lockToAuctioneer } = options;
    const pageType = getPageType(pathname);
    const crumbs = [];

    // Don't show breadcrumbs on list pages
    if (pageType === 'home' || pageType === 'auctions' || pageType === 'auctioneers') {
        return crumbs;
    }

    // Add home crumb
    if (!lockToAuctioneer) {
        crumbs.push({
            label: 'Auctions',
            url: getIndexUrl()
        });
    }

    // Add intermediate crumb for detail pages
    if (pageType === 'auctioneer' && !lockToAuctioneer) {
        crumbs.push({
            label: 'Auctioneers',
            url: getAuctioneerListUrl()
        });
    }

    // Add current page (no link)
    if (pageTitle) {
        crumbs.push({
            label: pageTitle,
            url: null
        });
    }

    return crumbs;
};
